import React from 'react';
import { ArrowLeftIcon, CreditCardIcon, CalendarIcon, DollarSignIcon, BuildingIcon, FileTextIcon, UserIcon, ClockIcon, ServerIcon, MessageSquareIcon, CheckIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import { Payment } from './PaymentsView';
import { formatCurrency } from '../../utils/currencyFormatter';
interface PaymentDetailProps {
  payment: Payment;
  onBack: () => void;
  locale: string;
}
export function PaymentDetail({
  payment,
  onBack,
  locale
}: PaymentDetailProps) {
  // Format dates for display
  const formattedPaymentDate = new Date(payment.paymentDate).toLocaleDateString(locale);
  const formattedCreatedAt = new Date(payment.createdAt).toLocaleString(locale);
  // Calculate total invoice amount
  const totalInvoiceAmount = payment.linkedInvoices.reduce((sum, invoice) => sum + invoice.invoiceTotal, 0);
  // Calculate total amount paid
  const totalAmountPaid = payment.linkedInvoices.reduce((sum, invoice) => sum + invoice.amountPaid, 0);
  // Check if this is a multi-vendor payment
  const uniqueVendors = new Set(payment.linkedInvoices.map(inv => inv.vendorId));
  const isMultiVendor = uniqueVendors.size > 1;
  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Cleared':
        return <CheckIcon className="h-4 w-4 text-green-500" data-id="element-10711" />;
      case 'Pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" data-id="element-10712" />;
      case 'Failed':
        return <XIcon className="h-4 w-4 text-red-500" data-id="element-10713" />;
      case 'Cancelled':
        return <AlertTriangleIcon className="h-4 w-4 text-gray-500" data-id="element-10714" />;
      default:
        return null;
    }
  };
  return <div className="bg-white shadow rounded-lg" data-id="element-10715">
      {/* Header */}
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-10716">
        <div className="flex items-center" data-id="element-10717">
          <button onClick={onBack} className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10718">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-10719" />
          </button>
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10720">
            Payment Details
          </h3>
        </div>
        <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${payment.status === 'Cleared' ? 'bg-green-100 text-green-800' : payment.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : payment.status === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10721">
          {payment.status}
        </span>
      </div>

      <div className="px-4 py-5 sm:px-6" data-id="element-10722">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10723">
          {/* Payment Header Info Section */}
          <div className="sm:col-span-3" data-id="element-10724">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10725">
              <CreditCardIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10726" />
              Payment Information
            </h4>
          </div>

          <div className="sm:col-span-1" data-id="element-10727">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10728">
              Payment Reference
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10729">
              {payment.paymentReference}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10730">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10731">Payment Date</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-10732">
              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10733" />
              {formattedPaymentDate}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10734">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10735">
              Payment Method
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10736">{payment.method}</dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10737">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10738">
              Payment Amount
            </dt>
            <dd className="mt-1 text-sm text-gray-900 font-semibold flex items-center" data-id="element-10739">
              <DollarSignIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10740" />
              {formatCurrency(payment.amount, payment.currency, locale)}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10741">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10742">Currency</dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10743">{payment.currency}</dd>
          </div>

          {payment.externalSourceId && <div className="sm:col-span-1" data-id="element-10744">
              <dt className="text-sm font-medium text-gray-500" data-id="element-10745">
                External Source ID
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-10746">
                {payment.externalSourceId}
              </dd>
            </div>}

          {/* Linked Invoices Section */}
          <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-10747">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10748">
              <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10749" />
              Linked Invoices ({payment.linkedInvoices.length})
            </h4>
          </div>

          <div className="sm:col-span-3" data-id="element-10750">
            <div className="overflow-x-auto" data-id="element-10751">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-10752">
                <thead className="bg-gray-50" data-id="element-10753">
                  <tr data-id="element-10754">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10755">
                      Invoice Number
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10756">
                      Vendor
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10757">
                      Invoice Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10758">
                      Invoice Total
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10759">
                      Amount Paid
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10760">
                      Remaining Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-10761">
                  {payment.linkedInvoices.map(invoice => <tr key={invoice.invoiceId} className="hover:bg-gray-50" data-id="element-10762">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-10763">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10764">
                        {invoice.vendorName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10765">
                        {new Date(invoice.invoiceDate).toLocaleDateString(locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10766">
                        {formatCurrency(invoice.invoiceTotal, payment.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10767">
                        {formatCurrency(invoice.amountPaid, payment.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" data-id="element-10768">
                        <span className={invoice.remainingBalance === 0 ? 'text-green-600' : 'text-yellow-600'} data-id="element-10769">
                          {formatCurrency(invoice.remainingBalance || 0, payment.currency, locale)}
                          {invoice.remainingBalance === 0 && <CheckIcon className="inline-block h-4 w-4 ml-1 text-green-500" data-id="element-10770" />}
                        </span>
                      </td>
                    </tr>)}
                  {/* Summary row */}
                  <tr className="bg-gray-50 font-medium" data-id="element-10771">
                    <td colSpan={3} className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-10772">
                      Totals:
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10773">
                      {formatCurrency(totalInvoiceAmount, payment.currency, locale)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10774">
                      {formatCurrency(totalAmountPaid, payment.currency, locale)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10775">
                      {formatCurrency(totalInvoiceAmount - totalAmountPaid, payment.currency, locale)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {isMultiVendor && <div className="mt-2 bg-yellow-50 p-2 rounded-md text-sm text-yellow-700 flex items-center" data-id="element-10776">
                <AlertTriangleIcon className="h-4 w-4 mr-2" data-id="element-10777" />
                This payment is allocated across multiple vendors
              </div>}

            {payment.status === 'Cleared' && totalAmountPaid < totalInvoiceAmount && <div className="mt-2 bg-blue-50 p-2 rounded-md text-sm text-blue-700 flex items-center" data-id="element-10778">
                  <InfoIcon className="h-4 w-4 mr-2" data-id="element-10779" />
                  This payment partially covers the linked invoices. Additional
                  payments will be needed to clear the remaining balance.
                </div>}
          </div>

          {/* Metadata Section */}
          <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-10780">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10781">
              <BuildingIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10782" />
              Metadata
            </h4>
          </div>

          <div className="sm:col-span-1" data-id="element-10783">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10784">
              Organisation Unit
            </dt>
            <dd className="mt-1 text-sm text-gray-900" data-id="element-10785">
              {payment.orgUnitName}
            </dd>
          </div>

          <div className="sm:col-span-2" data-id="element-10786">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10787">
              Organisation Path
            </dt>
            <dd className="mt-1 text-sm text-gray-500" data-id="element-10788">
              {payment.orgUnitPath}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10789">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10790">Created At</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-10791">
              <ClockIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10792" />
              {formattedCreatedAt}
            </dd>
          </div>

          <div className="sm:col-span-1" data-id="element-10793">
            <dt className="text-sm font-medium text-gray-500" data-id="element-10794">Source System</dt>
            <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-10795">
              <ServerIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-10796" />
              {payment.sourceSystem}
            </dd>
          </div>

          {/* Notes Section (if available) */}
          {payment.notes && <>
              <div className="sm:col-span-3 border-t pt-6 mt-2" data-id="element-10797">
                <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center" data-id="element-10798">
                  <MessageSquareIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-10799" />
                  Notes
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-10800">
                <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md" data-id="element-10801">
                  {payment.notes}
                </dd>
              </div>
            </>}
        </dl>
      </div>
    </div>;
}
// For the InfoIcon in the component
function InfoIcon(props) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-10802">
      <circle cx="12" cy="12" r="10" data-id="element-10803" />
      <line x1="12" y1="16" x2="12" y2="12" data-id="element-10804" />
      <line x1="12" y1="8" x2="12.01" y2="8" data-id="element-10805" />
    </svg>;
}